import { Link } from "react-router-dom"
import { AlertCircle, Loader2 } from "lucide-react"
import type { LeaseIntakeRun, LeaseIntakeStage } from "@/app/api/leaseIntakes"
import type { ClientTheme } from "@/app/lib/themes"
import { secondaryButton } from "@/app/lib/buttonStyles"
import { Card, mutedText } from "@/app/pages/LeaseIntakePage/shared"

const STAGE_LABELS: Record<LeaseIntakeStage, string> = {
  survey: "Surveying the document",
  slices: "Extracting leases",
  verify: "Verifying legal descriptions",
  done: "Finishing up",
}

// Queued / extracting / committing: stage label, slice x/y, and running found
// count. The run query polls every 2s while in these states.
export function ProgressPanel({ run, theme }: { run: LeaseIntakeRun; theme: ClientTheme }) {
  const { stage, current, total, found } = run.progress ?? {}
  const committing = run.status === "committing"
  const label =
    run.status === "queued"
      ? "Waiting to start"
      : committing
        ? "Adding leases to the Lease Schedule"
        : stage
          ? STAGE_LABELS[stage]
          : "Starting extraction"
  const hasSlices = !committing && typeof total === "number" && total > 0
  const pct = hasSlices ? Math.min(100, Math.round(((current ?? 0) / total) * 100)) : null

  return (
    <Card theme={theme}>
      <div className="flex items-center gap-2 mb-3">
        <Loader2 size={16} className="animate-spin" style={{ color: theme.brandPrimary }} />
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "14px",
            fontWeight: 600,
            color: theme.textPrimary,
          }}
        >
          {label}
          {hasSlices && ` — slice ${current ?? 0} of ${total}`}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct ?? undefined}
        className="rounded-full overflow-hidden"
        style={{ height: "6px", backgroundColor: theme.bgSurface2 }}
      >
        <div
          className={pct === null ? "animate-pulse" : ""}
          style={{
            height: "100%",
            width: pct === null ? "35%" : `${pct}%`,
            backgroundColor: theme.brandPrimary,
            transition: "width 400ms ease",
          }}
        />
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3" style={mutedText(theme)}>
        <span>
          <strong style={{ color: theme.textPrimary }}>{found ?? 0}</strong> lease
          {found === 1 ? "" : "s"} found so far
        </span>
        {run.page_count != null && <span>{run.page_count} pages</span>}
        {run.estimated_leases != null && <span>~{run.estimated_leases} expected</span>}
      </div>
      <div style={{ ...mutedText(theme, "11px"), color: theme.textDim, marginTop: "10px" }}>
        You can leave this page — the extraction keeps running and the run stays in Recent imports.
      </div>
    </Card>
  )
}

export function FailedPanel({ run, theme }: { run: LeaseIntakeRun; theme: ClientTheme }) {
  return (
    <Card theme={theme}>
      <div className="flex items-start gap-2">
        <AlertCircle
          size={16}
          style={{ color: theme.brandAccent, marginTop: "2px", flexShrink: 0 }}
        />
        <div className="flex-1">
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "14px",
              fontWeight: 600,
              color: theme.brandAccent,
            }}
          >
            Extraction failed
          </div>
          <div style={{ ...mutedText(theme, "13px"), marginTop: "4px", whiteSpace: "pre-wrap" }}>
            {run.error || "The document could not be processed."}
          </div>
        </div>
      </div>
      <div className="mt-4 flex">
        <Link to="/leases/intake" className="btn" style={secondaryButton(theme)}>
          Try another file
        </Link>
      </div>
    </Card>
  )
}
